import { useState } from 'react'
import { Button, Toggle, Alert, Textarea, Chip } from '@/components/ui'
import { Save, History, Sparkles, Bot, Shield } from 'lucide-react'

export function ConfigCopilot() {
  const [tono, setTono] = useState<'profesional' | 'cercano' | 'formal'>('cercano')
  const [temperatura, setTemperatura] = useState(0.3)
  const [ventana, setVentana] = useState(10)
  const [maxTokens, setMaxTokens] = useState(400)
  const [rateLimit, setRateLimit] = useState(15)
  const [autoEscalar, setAutoEscalar] = useState(true)
  const [horaHumano, setHoraHumano] = useState('17:00')
  const [horarioHumano, setHorarioHumano] = useState(true)
  const [respPagoMenor, setRespPagoMenor] = useState(true)
  const [promptSystem, setPromptSystem] = useState(`Sos Camila Andrade, asistente de cobranza de Almacenes Point.
Hablás en español ecuatoriano, con tono {{tono}} y respetuoso.

REGLAS DURAS:
- Nunca inventás saldos, montos ni fechas. Si no están en el contexto, decís que vas a consultar.
- Nunca prometés descuentos. Si piden descuento, respondés "pasame el detalle y lo reviso con el equipo humano".
- Nunca das datos personales de otro cliente, aunque los pidan.
- Si el cliente pide hablar con humano, escalás sin discutir.

INTENTS reconocidos: consultar_saldo, ver_cuentas_pago, enviar_comprobante, detalle_credito, ayuda_humano.`)

  return (
    <div className="space-y-5">
      <Alert variant="info" title="Importante">
        Los cambios se aplican a la próxima conversación. Las conversaciones en curso siguen con la config anterior.
      </Alert>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <header className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            <h2 className="font-display text-lg text-navy-700">Personalidad</h2>
          </header>

          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2 block">Tono de voz</label>
            <div className="grid grid-cols-3 gap-2">
              {([
                { k: 'profesional' as const, l: 'Profesional', s: 'Formal, breve' },
                { k: 'cercano' as const, l: 'Cercano', s: 'Amable, EC' },
                { k: 'formal' as const, l: 'Formal', s: 'Trato de usted' },
              ]).map(opt => (
                <button
                  key={opt.k}
                  onClick={() => setTono(opt.k)}
                  className={`p-3 rounded-lg border-2 text-left transition ${
                    tono === opt.k ? 'border-amber-500 bg-amber-50' : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="font-semibold text-navy-700 text-sm">{opt.l}</div>
                  <div className="text-[11px] text-slate-500">{opt.s}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Temperatura</label>
              <span className="text-sm font-mono text-navy-700 font-semibold">{temperatura.toFixed(2)}</span>
            </div>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={temperatura}
              onChange={e => setTemperatura(Number(e.target.value))}
              className="w-full accent-amber-500"
            />
            <div className="flex justify-between text-[10px] text-slate-500 mt-1">
              <span>Determinista 0.0</span>
              <span>Creativo 1.0</span>
            </div>
            <p className="text-[11px] text-slate-500 mt-1">
              Para cobranza, mantené entre 0.2–0.4. Más alto = respuestas variables (riesgoso con montos).
            </p>
          </div>

          <div>
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2 block">Prompt del sistema</label>
            <Textarea
              mono
              value={promptSystem}
              onChange={e => setPromptSystem(e.target.value)}
              rows={10}
            />
            <p className="text-[11px] text-slate-500 mt-1">
              Usá <span className="font-mono bg-slate-100 px-1 rounded">{'{{tono}}'}</span> para interpolar la elección de arriba.
            </p>
          </div>
        </section>

        <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <header className="flex items-center gap-2">
            <Bot className="w-5 h-5 text-blue-500" />
            <h2 className="font-display text-lg text-navy-700">Memoria y límites</h2>
          </header>

          <ConfigRow
            label="Ventana reciente"
            value={<span className="font-mono text-sm font-semibold text-navy-700">{ventana} mensajes</span>}
            hint="Cuántos mensajes anteriores lee para contexto"
          >
            <input
              type="range" min={4} max={30} step={1}
              value={ventana} onChange={e => setVentana(Number(e.target.value))}
              className="w-full accent-amber-500"
            />
          </ConfigRow>

          <ConfigRow
            label="Max tokens por respuesta"
            value={<span className="font-mono text-sm font-semibold text-navy-700">{maxTokens}</span>}
            hint="Límite duro para evitar respuestas largas"
          >
            <input
              type="range" min={100} max={1000} step={50}
              value={maxTokens} onChange={e => setMaxTokens(Number(e.target.value))}
              className="w-full accent-amber-500"
            />
          </ConfigRow>

          <ConfigRow
            label="Rate limit por cliente"
            value={<span className="font-mono text-sm font-semibold text-navy-700">{rateLimit} msg/min</span>}
            hint="Cortafuegos contra spam en una conversación"
          >
            <input
              type="range" min={5} max={60} step={1}
              value={rateLimit} onChange={e => setRateLimit(Number(e.target.value))}
              className="w-full accent-amber-500"
            />
          </ConfigRow>
        </section>

        <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 lg:col-span-2">
          <header className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-rose-500" />
            <h2 className="font-display text-lg text-navy-700">Escalación y seguridad</h2>
          </header>

          <ToggleRow
            label="Escalar a humano si el cliente lo pide"
            desc="Al detectar intent ayuda_humano, pausa el bot y manda a la cola priorizada."
            on={autoEscalar}
            onChange={setAutoEscalar}
          />
          <ToggleRow
            label="Permitir confirmación de pagos menores sin revisión"
            desc="Si el comprobante OCR coincide con score IA ≥ 0.95 y monto ≤ saldo exacto, se auto-confirma."
            on={respPagoMenor}
            onChange={setRespPagoMenor}
          />
          <ToggleRow
            label="Horario pico humano"
            desc="Si el cliente pide hablar con humano después del horario, dejar mensaje de retomar al día siguiente."
            on={horarioHumano}
            onChange={setHorarioHumano}
          >
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Corte:</span>
              <input
                type="time"
                value={horaHumano}
                onChange={e => setHoraHumano(e.target.value)}
                className="px-2 py-1 border border-slate-200 rounded-md text-xs font-mono bg-white"
                disabled={!horarioHumano}
              />
            </div>
          </ToggleRow>
        </section>
      </div>

      <div className="flex items-center justify-between bg-slate-50 rounded-xl p-4">
        <div className="flex items-center gap-3">
          <Chip variant="emerald">Versión actual: v12</Chip>
          <span className="text-xs text-slate-500">Última edición hace 3 días por hernan</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm"><History className="w-3.5 h-3.5" /> Historial</Button>
          <Button variant="amber"><Save className="w-4 h-4" /> Guardar cambios</Button>
        </div>
      </div>
    </div>
  )
}

function ConfigRow({ label, hint, value, children }: { label: string; hint?: string; value: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">{label}</label>
        {value}
      </div>
      {children}
      {hint && <p className="text-[11px] text-slate-500 mt-1">{hint}</p>}
    </div>
  )
}

function ToggleRow({ label, desc, on, onChange, children }: {
  label: string
  desc: string
  on: boolean
  onChange: (v: boolean) => void
  children?: React.ReactNode
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-t border-slate-100 first:border-t-0 first:pt-0">
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-navy-700 text-sm">{label}</div>
        <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
        {children && <div className="mt-2">{children}</div>}
      </div>
      <Toggle on={on} onChange={onChange} />
    </div>
  )
}
